// Commission maths for affiliate campaigns.
//
// Pure by design: no Firebase, no React, no moment. Every number that reaches a KPI card,
// a chart, the campaign list or the CSV comes through here, so the euro figure an admin
// reads and the one a partner gets invoiced for cannot drift apart.
//
// Commission is computed on the fly from the campaign's CURRENT rules against the LIVE
// sale documents - there are no attribution records. Two consequences worth knowing:
// cancellations and refunds correct themselves for free (a cancelled sale simply stops
// counting), but editing a rule restates every past period, which is why the editor warns
// before changing a rule on a campaign that already has sales.
//
// Input rows are the normalised shape produced by affiliateSales.js - amounts in EUR.

export const SALE_TYPES = [
  { key: 'reservations', label: 'Varaukset' },
  { key: 'eventtickets', label: 'Tapahtumaliput' },
  { key: 'activities', label: 'Aktiviteetit' },
]

export const SALE_TYPE_KEYS = SALE_TYPES.map((t) => t.key)

export const saleTypeLabel = (key) => {
  const found = SALE_TYPES.find((t) => t.key === key)
  return found ? found.label : key
}

// A rule that earns nothing. Spread over stored rules so a campaign written before a
// field existed still reads as a complete rule.
export const EMPTY_RULE = {
  enabled: false,
  type: 'percent', // 'percent' | 'fixed'
  value: 0, // percent (10 = 10 %) or EUR when fixed
  basis: 'gross', // percent only: 'gross' (incl. VAT) | 'net' (excl. VAT)
  per: 'sale', // fixed only: 'sale' | 'unit' (per ticket / per participant)
  minorder: 0, // EUR the sale must reach to earn anything; 0 = no minimum
  maxpersale: 0, // EUR cap on one sale's commission; 0 = no cap
}

export const emptyCommission = () => ({
  reservations: { ...EMPTY_RULE },
  eventtickets: { ...EMPTY_RULE },
  activities: { ...EMPTY_RULE },
})

// Stored campaigns are merge-written and may predate a field, so never read
// campaign.commission[type] directly - go through this.
export const ruleFor = (campaign, type) => ({
  ...EMPTY_RULE,
  ...((campaign && campaign.commission && campaign.commission[type]) || {}),
})

const num = (value) => {
  if (typeof value === 'number') return isFinite(value) ? value : 0
  if (value === null || value === undefined || value === '') return 0
  const parsed = parseFloat(String(value).replace(',', '.').replace(/[^0-9.-]/g, ''))
  return isFinite(parsed) ? parsed : 0
}

// Cents, not banker rounding: this is money on an invoice.
export const round2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100

// VAT in this codebase is a percentage on top of a VAT-inclusive price, so the net is the
// gross divided out rather than a separate stored field.
export const netFromGross = (gross, vat) => {
  const v = num(vat)
  if (v <= 0) return round2(gross)
  return round2(num(gross) / (1 + v / 100))
}

// What one sale earns. Cancelled sales earn nothing - that is the whole refund story under
// compute-on-the-fly.
export const commissionForSale = (row, rule) => {
  if (!row || row.cancelled) return 0
  if (!rule || !rule.enabled) return 0

  const gross = num(row.gross)
  const minorder = num(rule.minorder)
  // The threshold is on the sale gross regardless of the rule basis: it answers "is this
  // order big enough to pay for", which is a question about the till, not about VAT.
  if (minorder > 0 && gross < minorder) return 0

  let commission = 0
  if (rule.type === 'fixed') {
    // 'unit' pays per ticket or per participant; a reservation is always one unit.
    const units = rule.per === 'unit' ? Math.max(1, Math.round(num(row.quantity)) || 1) : 1
    commission = num(rule.value) * units
  } else {
    const base = rule.basis === 'net' ? num(row.net) : gross
    commission = (base * num(rule.value)) / 100
  }

  if (commission < 0) commission = 0
  const cap = num(rule.maxpersale)
  if (cap > 0 && commission > cap) commission = cap
  return round2(commission)
}

// Commission per row, attached as `commission`. Kept separate from summariseSales so the
// DataGrid and the CSV show the same per-row figure the totals are built from.
export const withCommissions = (rows, campaign) =>
  (rows || []).map((row) => ({ ...row, commission: commissionForSale(row, ruleFor(campaign, row.type)) }))

const emptyBucket = () => ({ count: 0, quantity: 0, gross: 0, net: 0, commission: 0 })

const addToBucket = (bucket, row) => {
  bucket.count += 1
  bucket.quantity += Math.round(num(row.quantity)) || 1
  bucket.gross = round2(bucket.gross + num(row.gross))
  bucket.net = round2(bucket.net + num(row.net))
  bucket.commission = round2(bucket.commission + num(row.commission))
}

// Everything the report and the campaign list need, from one pass over the rows.
// Cancelled rows are counted separately and never reach the totals.
export const summariseSales = (rows, campaign) => {
  const priced = withCommissions(rows, campaign)

  const byType = {}
  SALE_TYPE_KEYS.forEach((key) => {
    byType[key] = emptyBucket()
  })
  const byCode = {}
  const total = emptyBucket()
  const cancelled = { count: 0, gross: 0 }

  priced.forEach((row) => {
    if (row.cancelled) {
      cancelled.count += 1
      cancelled.gross = round2(cancelled.gross + num(row.gross))
      return
    }
    if (!byType[row.type]) byType[row.type] = emptyBucket()
    addToBucket(byType[row.type], row)
    const code = row.code || ''
    if (!byCode[code]) byCode[code] = emptyBucket()
    addToBucket(byCode[code], row)
    addToBucket(total, row)
  })

  total.average = total.count > 0 ? round2(total.gross / total.count) : 0

  // The cap limits what is payable, not what was earned - an admin needs to see the
  // overrun, not a silently clamped number.
  const cap = num(campaign && campaign.budgetcap)
  const budget =
    cap > 0
      ? {
          cap,
          earned: total.commission,
          payable: round2(Math.min(total.commission, cap)),
          remaining: round2(Math.max(0, cap - total.commission)),
          exceeded: total.commission > cap,
        }
      : null

  return { rows: priced, byType, byCode, total, cancelled, budget }
}

// Daily and cumulative series for the report timeline charts. Dates are the normalised
// rows' 'YYYY-MM-DD' purchase dates, so this buckets on when the sale HAPPENED, not on
// when the court is booked or the event is held.
export const salesTimeline = (rows) => {
  const byDate = {}
  const list = rows || []
  list.forEach((row) => {
    if (row.cancelled || !row.date) return
    if (!byDate[row.date]) byDate[row.date] = { count: 0, gross: 0, commission: 0 }
    byDate[row.date].count += 1
    byDate[row.date].gross = round2(byDate[row.date].gross + num(row.gross))
    byDate[row.date].commission = round2(byDate[row.date].commission + num(row.commission))
  })

  const dates = Object.keys(byDate).sort()
  let runningGross = 0
  let runningCommission = 0
  let runningCount = 0
  const cumulativeGross = []
  const cumulativeCount = []
  const dailyGross = []
  const dailyCommission = []
  const dailyCount = []

  dates.forEach((date) => {
    const day = byDate[date]
    runningGross = round2(runningGross + day.gross)
    runningCommission = round2(runningCommission + day.commission)
    runningCount += day.count
    dailyGross.push(day.gross)
    dailyCommission.push(day.commission)
    dailyCount.push(day.count)
    cumulativeGross.push(runningGross)
    cumulativeCount.push(runningCount)
  })

  return {
    dates,
    dailyGross,
    dailyCommission,
    dailyCount,
    cumulativeGross,
    cumulativeCount,
    totalCommission: runningCommission,
  }
}
